import { useReducer, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import effects from 'app/effects/effects'
import * as R from 'ramda'
import { mapToApi } from './UpdateInitialNotificationApiMapper'
import { createTranslationPayload } from '../../notification/submit/NotiTranslator'
import { useDefaultLanguage, getLangaugeId } from 'commons/useDefaultLanguage'
import { getOverrideTranslation } from '../../notification/CreateNotiLens'
import { errorTypes } from 'commons/error'
import { useIntl } from 'react-intl'

const initialState = {
  isLoading: false,
  values: undefined,
  errors: undefined,
}

const TYPE_LOADING = 'loading'
const TYPE_SUCCESS = 'success'
const TYPE_ERROR = 'error'
const TYPE_INIT = 'initial'

const submitReducer = (state, action) => {
  switch (action.type) {
    case TYPE_LOADING:
      return { isLoading: true, values: undefined, errors: undefined }
    case TYPE_SUCCESS:
      return { isLoading: false, values: action.values, errors: undefined }
    case TYPE_ERROR:
      return { isLoading: false, values: undefined, errors: action.errors }
    case TYPE_INIT:
      return initialState
    default:
      return state
  }
}

export default (onUpdateSuccess) => {
  const dispatch = useDispatch()
  const language = useDefaultLanguage()
  const defaultLanguageId = getLangaugeId(language)
  const { formatMessage } = useIntl()

  const [submitState, submitDispatch] = useReducer(submitReducer, initialState)

  useEffect(() => {
    if (submitState.isLoading === false && !R.isNil(submitState.values)) {
      submitDispatch({ type: TYPE_INIT })
      onUpdateSuccess(submitState.values)
    }
  }, [submitState.values, submitState.isLoading, onUpdateSuccess])

  useEffect(() => {
    if (submitState.errors) {
      dispatch(
        MessageAlert.showNetworkError(formatMessage)(
          submitState.errors,
          errorTypes.default,
        ),
      )
    }
  }, [dispatch, submitState.errors, formatMessage])

  const submit = useCallback(
    (submitModel) => (translations) => {
      return effects.apis
        .updateInitialNotification({
          data: mapToApi(submitModel)(translations),
        })
        .then(() => {
          submitDispatch({ type: TYPE_SUCCESS, values: submitModel })
        })
        .catch((errors) => {
          submitDispatch({ type: TYPE_ERROR, errors: errors })
        })
    },
    [submitDispatch],
  )

  const translateAndSubmit = useCallback(
    (submitModel) => {
      submitDispatch({ type: TYPE_LOADING })
      const overrideTranslation = getOverrideTranslation(submitModel)

      const getDefaultLanguageList = () => {
        const hasDefaultInOverrideTranslation = !R.isEmpty(
          R.filter(
            R.propEq('languageId', defaultLanguageId),
            overrideTranslation || [],
          ),
        )
        if (hasDefaultInOverrideTranslation) {
          return overrideTranslation
        } else {
          return [createTranslationPayload(defaultLanguageId)(submitModel)]
        }
      }
      const defaultLanguageList = getDefaultLanguageList()
      if (defaultLanguageList && defaultLanguageList[0]) {
        delete defaultLanguageList[0]['languageId']
      }

      submit(submitModel)(R.defaultTo(defaultLanguageList, overrideTranslation))
    },
    [defaultLanguageId, submit],
  )

  return {
    submitNoti: translateAndSubmit,
    isSubmitting: submitState.isLoading,
  }
}
